/**
 * Display helpers for published report cards, shared by the Exam Results list
 * section and the ReportCardDetail screen (native and web).
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface DayParts {
  day: number;
  month: number; // 0-11
  year: number;
}

/**
 * The calendar day of a date value. Date-only values ("2026-06-01" or
 * "2026-06-01T00:00:00") are read from the text so no timezone can shift them;
 * a timestamp carrying a zone (Z / +hh:mm) is shown in the device's local time.
 */
const dayParts = (value?: string | null): DayParts | null => {
  if (!value) return null;
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(value.trim());
  if (!hasZone) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (m) return { year: Number(m[1]), month: Number(m[2]) - 1, day: Number(m[3]) };
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
};

/** "12 Oct 2026"; empty when the value cannot be read. */
export const formatReportCardDate = (value?: string | null): string => {
  const p = dayParts(value);
  return p ? `${p.day} ${MONTHS[p.month]} ${p.year}` : '';
};

/** "1 Jun – 30 Sep 2026", or "1 Dec 2025 – 31 Mar 2026" when the period spans two years. */
export const formatReportCardPeriod = (start?: string | null, end?: string | null): string => {
  const s = dayParts(start);
  const e = dayParts(end);
  if (!s && !e) return '';
  if (!s) return formatReportCardDate(end);
  if (!e) return formatReportCardDate(start);
  const startText = s.year === e.year ? `${s.day} ${MONTHS[s.month]}` : `${s.day} ${MONTHS[s.month]} ${s.year}`;
  return `${startText} – ${e.day} ${MONTHS[e.month]} ${e.year}`;
};

const isNumber = (value?: number | null): value is number =>
  value !== null && value !== undefined && !Number.isNaN(Number(value));

/** "50.4" (at most two decimals, no trailing zeros); "—" when there is no value. */
export const formatMarks = (value?: number | null): string =>
  isNumber(value) ? String(Number(Number(value).toFixed(2))) : '—';

/** "412.5 / 500"; "—" when the marks are missing (the maximum alone says nothing). */
export const formatMarksOutOf = (marks?: number | null, maxMarks?: number | null): string => {
  if (!isNumber(marks)) return '—';
  return isNumber(maxMarks) ? `${formatMarks(marks)} / ${formatMarks(maxMarks)}` : formatMarks(marks);
};

/**
 * A grade band as whole marks out of 100, e.g. "80–89": the minimum rounded up and
 * the maximum rounded down (so 89.99 reads 89), except a maximum of 100 stays 100.
 */
export const formatBandRange = (minPercent: number, maxPercent: number): string => {
  const min = Math.ceil(Number(minPercent));
  const max = Number(maxPercent) >= 100 ? 100 : Math.floor(Number(maxPercent));
  return `${min}–${max}`;
};

/** "JD" for "Jane Doe"; first two letters for a single name. */
export const initialsOf = (name?: string | null): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

/** "Final Exam 50.4 / 70 · Test/Quiz 27 / 30" for a subject's parts (Grade Weightage). */
export const formatFactorBreakdown = (
  factors: { name: string; marks?: number | null; maxMarks?: number | null }[] | null | undefined,
): string => (factors ?? []).map(f => `${f.name} ${formatMarksOutOf(f.marks, f.maxMarks)}`).join(' · ');

/** Badge colours for a grade - same palette as the exam results table; blue for anything else. */
export const gradeBadgeColors = (grade?: string | null): { bg: string; text: string } => {
  const g = (grade ?? '').trim().toUpperCase();
  if (g === 'A+' || g === 'A' || g === 'A-') return { bg: '#DCFCE7', text: '#16A34A' };
  if (g === 'B+' || g === 'B' || g === 'B-') return { bg: '#E0F2FE', text: '#0284C7' };
  if (g === 'C+' || g === 'C' || g === 'C-') return { bg: '#FEF3C7', text: '#D97706' };
  if (g === 'D' || g === 'E' || g === 'F') return { bg: '#FEE2E2', text: '#DC2626' };
  return { bg: '#E0F2FE', text: '#0284C7' };
};

/** A filesystem-safe PDF name such as "Report-Card-Fall-2026-Jane-Doe.pdf". */
export const reportCardFileName = (configName?: string | null, studentName?: string | null): string => {
  const base = ['Report Card', configName, studentName]
    .filter(Boolean)
    .join(' ')
    .replace(/[^A-Za-z0-9 _-]+/g, '')
    .trim()
    .replace(/\s+/g, '-');
  return `${base || 'Report-Card'}.pdf`;
};

/** The server's {message} from a JSON error response, or the fallback. */
export const reportCardErrorMessage = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  if (data && typeof data === 'object' && typeof data.message === 'string' && data.message.trim()) {
    return data.message.trim();
  }
  if (typeof data === 'string' && data.trim() && data.length < 300) return data.trim();
  return fallback;
};
